import axios from '../../common/utils/interceptor';
import localizeContentMapper from '../../common/utils/localizeContentMapper';
import React, { useEffect, useState } from 'react';
import { apiUrls } from '../../constants';
import TaskListLayout01 from './layout_01/TaskListLayout';
import TaskListLayout02 from './layout_02/TaskListLayout';
import { GAPageViewMappings, GARouteMappings } from '../../constants/gaMappings';
import { sendPageViewDetails } from '../../common/utils/gaEvents';
import { Task, TaskListConfiguration, TaskListLayoutProps } from '../interfaces';
import { taskListDefaultConfiguration } from '../constants';
import { useBasicDetails } from '../../store/storeApi';
import { WorkflowItemCode } from '../../constants';
import { SessionStore } from '../../constants/sessionStore';
import useHotjar from '../../hotjar/useHotjar';
import { useMediaQuery, useTheme } from '@material-ui/core';
import { getAssessmentSupportedOnDesktop } from '../utils/getAssessmentSupportedOnDesktop';
import markSchedulableItems from '../utils/markSchedulableItems';
import taskItemStatusMapper from '../utils/taskItemStatusMapper';
import { getNextLinearTask } from '../utils/getNextLinearTask';
import { initializeTask } from '../utils/initializeTask';
import { useHistory, useLocation } from 'react-router-dom';
import { FullPageLoader } from '../../common/components';
import { getTemplateLayout } from '../../common/utils/FallbackCondition';

let timeoutId: NodeJS.Timeout;
const TaskListLayoutComponentsMap: { [key: string]: React.FC<TaskListLayoutProps> } = {
  TEMPLATE_LAYOUT_01: TaskListLayout01,
  TEMPLATE_LAYOUT_02: TaskListLayout02,
};
/**
 * TaskListPage
 */
const TaskListPage = (): JSX.Element => {
  /** Page View GA method */
  useEffect(() => {
    sendPageViewDetails(GARouteMappings.TASKLIST, GAPageViewMappings.TASKLIST);
  }, []);

  const {
    currentWorkflowItem,
    headerDetails: { isICPEnabled },
    userSessionActive,
    template,
  } = useBasicDetails();
  const { code, configuration, localizedContentMap, nestedItemsState } = currentWorkflowItem;

  const parsedConfig =
    configuration && code === WorkflowItemCode.TASKLIST
      ? JSON.parse(configuration).configWidgetJSON
      : taskListDefaultConfiguration;
  const taskListConfiguration: TaskListConfiguration = localizeContentMapper(parsedConfig, localizedContentMap);
  const [schedulableAssessments, setSchedulableAssessments] = useState([]);
  const [showTileBanner, setShowTileBanner] = useState(false);
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const smDown = useMediaQuery(theme.breakpoints.down('xs'));
  const [desktopAssessments, setDesktopAssessments] = useState<boolean>(false);
  const { surveyEnabled, surveyTriggerCode, linearflow, showTaskList } = taskListConfiguration;

  const tasksWithScheduledAssessment = markSchedulableItems(taskListConfiguration.tasks, schedulableAssessments);
  const tasks = taskItemStatusMapper(tasksWithScheduledAssessment, nestedItemsState);
  const isAssessmentPresent = tasks.some((task) => task.containAssessmentItem);

  const getSchedulableAssessments = async (): Promise<void> => {
    try {
      const { data } = await axios.get(apiUrls.GET_SCHEDULABLE_ASSESSMENTS);
      sessionStorage.setItem(SessionStore.schedulableAssessments, JSON.stringify(data));
      setSchedulableAssessments(data);
      setShowTileBanner(true);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    getSchedulableAssessments();
  }, []);

  useEffect(() => {
    if (smDown && isAssessmentPresent) {
      const getDesktopBoolValue = async (): Promise<void> => {
        try {
          const response = await getAssessmentSupportedOnDesktop();
          if (response) {
            setDesktopAssessments(response);
          }
        } catch (e) {
          console.error(`Error in fetching the desktop assessment details`, e);
        }
      };

      getDesktopBoolValue();
    }
  }, [smDown, isAssessmentPresent]);

  useHotjar(surveyEnabled, surveyTriggerCode);

  const [linearTask, setLinearTask] = useState<Task | undefined>();
  const [isTaskInitialized, setIsTaskInitialized] = useState<boolean>(false);
  useEffect(() => {
    if (linearflow && !showTaskList && !linearTask) {
      const allowRedirect = sessionStorage.getItem(SessionStore.allowRedirectToTasklist) === 'true';
      if (!allowRedirect) {
        setLinearTask(getNextLinearTask(tasks));
      }
    }
  }, [tasks, linearflow, showTaskList, linearTask]);

  useEffect(() => {
    clearTimeout(timeoutId);

    timeoutId = setTimeout(async () => {
      if (linearTask) {
        await initializeTask(linearTask, userSessionActive, history, location);
        setIsTaskInitialized(true);
      }
    }, 200);
  }, [linearTask, history, location, userSessionActive]);

  if (linearTask && !isTaskInitialized) return <FullPageLoader isLoading />;
  const layout = getTemplateLayout(template.layoutCode);
  return (
    <>
      {TaskListLayoutComponentsMap[layout] &&
        React.createElement(TaskListLayoutComponentsMap[layout], {
          taskListConfiguration,
          showTileBanner,
          isICPEnabled,
          desktopAssessments,
          tasks,
        })}
    </>
  );
};

export default TaskListPage;
